import { ReactNode, CSSProperties, useState } from "react";
import { t, avatarColorFromString, hueFromString } from "@/styles/design-system";

// Re-export the design-system token bag so existing imports keep working.
export { t };

// Status palette (uses design-system tokens)
export const STATUS_COLORS: Record<string, string> = {
  invited: t.warning,
  confirmed: t.info,
  assets_received: t.purple,
  ready: t.success,
  declined: t.danger,
  canceled: t.danger,
};

export const STATUS_LABELS: Record<string, string> = {
  invited: "Invited",
  confirmed: "Confirmed",
  assets_received: "Assets Received",
  ready: "Ready",
  declined: "Declined",
  canceled: "Canceled",
};

// Format colors (tuned to harmonise with the indigo brand)
const FORMAT_COLOR_MAP: Record<string, string> = {
  keynote:    "#2563eb",
  panel:      "#7c3aed",
  workshop:   "#10b981",
  fireside:   "#f59e0b",
  interview:  "#db2777",
  roundtable: "#0891b2",
  sponsor:    "#ef4444",
  training:   "#65a30d",
};
const FORMAT_COLOR_PALETTE = ["#2563eb","#7c3aed","#10b981","#f59e0b","#ef4444","#0891b2","#db2777","#65a30d","#ea580c","#0d9488"];
export function formatColor(format: string): string {
  if (FORMAT_COLOR_MAP[format]) return FORMAT_COLOR_MAP[format];
  const idx = format.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % FORMAT_COLOR_PALETTE.length;
  return FORMAT_COLOR_PALETTE[idx];
}
/** @deprecated use formatColor(format) instead */
export const FORMAT_COLORS: Record<string, string> = FORMAT_COLOR_MAP;

// SVG icon paths (unchanged inventory). We keep this in place for backwards
// compatibility; new code can also import from lucide-react.
export const ICONS: Record<string, string> = {
  grid: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z",
  users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  calendar: "M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM16 2v4M8 2v4M3 10h18",
  mail: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6",
  alert: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01",
  check: "M20 6L9 17l-5-5",
  x: "M18 6L6 18M6 6l12 12",
  search: "M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM21 21l-4.35-4.35",
  plus: "M12 5v14M5 12h14",
  clock: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 6v6l4 2",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6",
  send: "M22 2L11 13M22 2l-7 20-4-9-9-4z",
  chevronRight: "M9 18l6-6-6-6",
  chevronDown: "M6 9l6 6 6-6",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  eyeOff: "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22",
  edit: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
  trash: "M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
  globe: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
  upload: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12",
  activity: "M22 12h-4l-3 9L9 3l-3 9H2",
  grip: "M9 4h1v1H9zM9 8h1v1H9zM9 12h1v1H9zM14 4h1v1h-1zM14 8h1v1h-1zM14 12h1v1h-1z",
  list: "M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  bell: "M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0",
  sparkles: "M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5zM19 13l.75 2.25L22 16l-2.25.75L19 19l-.75-2.25L16 16l2.25-.75zM5 16l.75 2.25L8 19l-2.25.75L5 22l-.75-2.25L2 19l2.25-.75z",
  mapPin: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  hash: "M4 9h16M4 15h16M10 3L8 21M16 3l-2 18",
  star: "M12 2l3.09 6.26 6.91 1.01-5 4.87 1.18 6.88L12 17.77l-6.18 3.25 1.18-6.88-5-4.87 6.91-1.01L12 2z",
};

export function Icon({ d, size = 16, color = "currentColor", strokeWidth = 2 }: { d: string; size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

// Badge — pill with soft background tint of the color
export function Badge({ children, color = t.accent, small = false }: { children: ReactNode; color?: string; small?: boolean }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: small ? "2px 8px" : "3px 10px",
      borderRadius: t.radiusPill,
      fontSize: small ? 10 : 11, fontWeight: 600,
      background: color + "18", color,
      letterSpacing: "0.02em",
      fontFamily: t.fontMono, textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

// StatusDot with subtle glow
export function StatusDot({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || t.muted;
  return <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}80`, display: "inline-block", flexShrink: 0 }} />;
}

// Avatar — initials over a saturated HSL fill, white text on top
export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  const initials = name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const bg = avatarColorFromString(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: bg, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700,
      fontFamily: t.fontSans, flexShrink: 0, letterSpacing: "-0.02em",
      boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    }}>
      {initials}
    </div>
  );
}

// Card — surface with soft shadow, optional hover lift via onClick
export function Card({ children, style: sx, onClick, active, hoverable }: {
  children: ReactNode;
  style?: CSSProperties;
  onClick?: () => void;
  active?: boolean;
  hoverable?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const shouldLift = (hoverable ?? !!onClick) && hover;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: t.surface,
        border: `1px solid ${active ? t.accent : t.border}`,
        borderRadius: t.radius,
        padding: 16,
        boxShadow: shouldLift ? t.shadowHover : t.shadow,
        transform: shouldLift ? "translateY(-1px)" : "translateY(0)",
        transition: `box-shadow ${t.transition}, transform ${t.transition}, border-color ${t.transition}`,
        cursor: onClick ? "pointer" : "default",
        ...sx,
      }}
    >
      {children}
    </div>
  );
}

// Stat card — icon tile + label + big number
export function Stat({ label, value, color, icon, sub, onClick }: {
  label: string; value: number | string; color?: string; icon?: string; sub?: string; onClick?: () => void;
}) {
  const c = color || t.accent;
  return (
    <Card style={{ flex: 1, minWidth: 140 }} onClick={onClick}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: t.radiusMd,
          background: c + "18",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `inset 0 0 0 1px ${c}28`,
        }}>
          {icon && <Icon d={ICONS[icon]} size={15} color={c} />}
        </div>
        <span style={{
          fontSize: 10, color: t.muted, fontWeight: 600, fontFamily: t.fontMono,
          textTransform: "uppercase", letterSpacing: "0.07em",
        }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: t.text, fontFamily: t.fontSans, lineHeight: 1, letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: t.muted, marginTop: 6 }}>{sub}</div>}
    </Card>
  );
}

// ProgressBar — rounded segmented bar
export function ProgressBar({ value, max, color = t.accent, height = 6 }: {
  value: number; max: number; color?: string; height?: number;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div style={{ height, background: t.border, borderRadius: height, overflow: "hidden", width: "100%" }}>
      <div style={{
        height: "100%", width: `${pct}%`, background: color,
        borderRadius: height, transition: "width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
      }} />
    </div>
  );
}

// Button — refined press effect, indigo primary, ghost / danger variants
type BtnVariant = "default" | "primary" | "ghost" | "danger";
export function Btn({
  children, onClick, variant = "default", small = false, icon, disabled, style: sx, type = "button",
}: {
  children?: ReactNode; onClick?: () => void; variant?: BtnVariant;
  small?: boolean; icon?: string; disabled?: boolean; style?: CSSProperties; type?: "button" | "submit";
}) {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);

  const base: CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: small ? "5px 11px" : "8px 14px",
    borderRadius: t.radiusMd, border: "1px solid",
    fontSize: small ? 12 : 13, fontWeight: 500,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: t.fontSans,
    transition: `background ${t.transitionFast}, border-color ${t.transitionFast}, color ${t.transitionFast}, transform ${t.transitionFast}, box-shadow ${t.transitionFast}`,
    whiteSpace: "nowrap",
    opacity: disabled ? 0.55 : 1,
    transform: press ? "translateY(1px)" : "translateY(0)",
    boxShadow: hover && !disabled && variant === "primary" ? "0 4px 12px rgba(99, 102, 241, 0.28)" : "none",
  };
  const variants: Record<BtnVariant, CSSProperties> = {
    default: {
      background: hover ? t.surfaceHover : t.surface,
      borderColor: hover ? t.borderStrong : t.border,
      color: t.text,
    },
    primary: {
      background: hover ? t.accentHover : t.accent,
      borderColor: hover ? t.accentHover : t.accent,
      color: "#fff",
    },
    ghost: {
      background: hover ? t.surfaceHover : "transparent",
      borderColor: "transparent",
      color: t.muted,
    },
    danger: {
      background: hover ? t.dangerSoft : t.dangerSoft + "80",
      borderColor: t.danger + "40",
      color: t.danger,
    },
  };
  return (
    <button
      type={type}
      style={{ ...base, ...variants[variant], ...sx }}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPress(false); }}
      onMouseDown={() => setPress(true)}
      onMouseUp={() => setPress(false)}
    >
      {icon && <Icon d={ICONS[icon]} size={small ? 13 : 14} />}
      {children}
    </button>
  );
}

// Tab — underline, indigo when active
export function Tab({ label, active, onClick, count }: { label: string; active: boolean; onClick: () => void; count?: number }) {
  return (
    <button onClick={onClick} style={{
      padding: "10px 0", background: "none", border: "none",
      borderBottom: `2px solid ${active ? t.accent : "transparent"}`,
      color: active ? t.text : t.muted, fontSize: 13, fontWeight: active ? 600 : 500,
      cursor: "pointer", fontFamily: t.fontSans,
      display: "flex", alignItems: "center", gap: 6,
      transition: `color ${t.transition}, border-color ${t.transition}`,
    }}>
      {label}
      {count != null && (
        <span style={{
          fontSize: 10, fontWeight: 700, fontFamily: t.fontMono,
          background: active ? t.accent + "20" : t.surfaceSunken,
          color: active ? t.accent : t.muted,
          padding: "2px 7px", borderRadius: t.radiusPill,
        }}>{count}</span>
      )}
    </button>
  );
}

// Input — focus ring uses the brand color
export function Input({ value, onChange, placeholder, icon, style: sx }: {
  value: string; onChange: (v: string) => void; placeholder?: string; icon?: string; style?: CSSProperties;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center", ...sx }}>
      {icon && <span style={{ position: "absolute", left: 12, color: t.muted, pointerEvents: "none", display: "flex" }}>
        <Icon d={ICONS[icon]} size={14} />
      </span>}
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%", padding: "9px 12px", paddingLeft: icon ? 34 : 12,
          background: t.surface,
          border: `1px solid ${focused ? t.accent : t.border}`,
          borderRadius: t.radiusMd, color: t.text, fontSize: 13,
          fontFamily: t.fontSans, outline: "none",
          boxShadow: focused ? t.focusRing : "none",
          transition: `border-color ${t.transitionFast}, box-shadow ${t.transitionFast}`,
        }}
      />
    </div>
  );
}

// Empty state
export function Empty({ icon, title, sub, action }: { icon: string; title: string; sub?: string; action?: ReactNode }) {
  return (
    <div style={{
      textAlign: "center", padding: "56px 24px", color: t.muted,
      background: t.surface, border: `1px dashed ${t.border}`, borderRadius: t.radius,
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: "50%",
        background: t.accentSoft,
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 16px",
      }}>
        <Icon d={ICONS[icon]} size={24} color={t.accent} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 6, letterSpacing: "-0.01em" }}>{title}</div>
      {sub && <div style={{ fontSize: 13, marginBottom: action ? 16 : 0 }}>{sub}</div>}
      {action && <div style={{ marginTop: 4 }}>{action}</div>}
    </div>
  );
}

// Loading spinner
export function Spinner({ size = 24 }: { size?: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 48 }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        border: `2px solid ${t.border}`, borderTopColor: t.accent,
        animation: "ui-spin 0.6s linear infinite",
      }} />
      <style>{`@keyframes ui-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Skeleton — animated placeholder for loading states
export function Skeleton({ width = "100%", height = 14, radius = 6, style: sx }: {
  width?: number | string; height?: number | string; radius?: number; style?: CSSProperties;
}) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: `linear-gradient(90deg, ${t.surfaceSunken} 0%, ${t.surfaceHover} 50%, ${t.surfaceSunken} 100%)`,
      backgroundSize: "200% 100%",
      animation: "ui-shimmer 1.4s linear infinite",
      ...sx,
    }}>
      <style>{`@keyframes ui-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }`}</style>
    </div>
  );
}

// Chip — small soft pill computed deterministically from a string (used for tags)
export function Chip({ children, color }: { children: string; color?: string }) {
  if (color) {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 10px", borderRadius: t.radiusPill,
        fontSize: 11, fontWeight: 500,
        background: color + "16", color,
        fontFamily: t.fontSans, whiteSpace: "nowrap",
      }}>{children}</span>
    );
  }
  const h = hueFromString(children);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: t.radiusPill,
      fontSize: 11, fontWeight: 500,
      background: `hsl(${h} 80% 95%)`, color: `hsl(${h} 50% 35%)`,
      fontFamily: t.fontSans, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}
